import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ArrowLeft, Crown, Shield, User, DollarSign, Users, Zap, CheckCircle, XCircle, Clock, Loader2, Copy, Gift, Info, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AppBranding from './AppBranding';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

const TIER_CONFIG = {
  FREE: { icon: <User className="h-5 w-5" />, color: 'border-gray-600 text-gray-400', badge: 'bg-gray-600', label: 'FREE' },
  PAID: { icon: <Zap className="h-5 w-5" />, color: 'border-blue-500 text-blue-400', badge: 'bg-blue-600', label: 'PAID' },
  FAMILY: { icon: <Users className="h-5 w-5" />, color: 'border-purple-500 text-purple-400', badge: 'bg-purple-600', label: 'FAMILY' },
  GOD: { icon: <Crown className="h-5 w-5" />, color: 'border-red-500 text-red-400', badge: 'bg-red-600', label: 'GOD' },
};

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [showReferralInfo, setShowReferralInfo] = useState(false);

  const sessionId = searchParams.get('session_id');
  const cancelled = searchParams.get('cancelled');

  const fetchSubscription = useCallback(async () => {
    try {
      const res = await axios.get(`${API_V2}/subscription/my-subscription`, { withCredentials: true });
      if (res.data.success) setSubscriptionData(res.data);
      // Fetch referral data
      try {
        const ref = await axios.get(`${API_V2}/referral/my-referrals`, { withCredentials: true });
        if (ref.data.success) setReferralData(ref.data);
      } catch {}
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    if (sessionId && !checkingPayment) {
      setCheckingPayment(true);
      let attempts = 0;
      const maxAttempts = 8;

      const poll = async () => {
        try {
          const res = await axios.get(`${API_V2}/subscription/status/${sessionId}`, { withCredentials: true });
          if (res.data.payment_status === 'paid') {
            toast.success('Subscription activated! Welcome to PAID tier.');
            setCheckingPayment(false);
            fetchSubscription();
            return;
          }
          if (res.data.stripe_status === 'expired') {
            toast.error('Payment session expired. Please try again.');
            setCheckingPayment(false);
            return;
          }
        } catch (err) {
          console.error('Poll error:', err);
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2500);
        } else {
          toast.error('Payment verification timed out. Check back shortly.');
          setCheckingPayment(false);
          fetchSubscription();
        }
      };
      poll();
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cancelled) toast.info('Payment cancelled. No charges were made.');
  }, [cancelled]);

  useEffect(() => { fetchSubscription(); }, [fetchSubscription]);

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API_V2}/subscription/checkout`, { origin_url: origin }, { withCredentials: true });
      if (res.data.success && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start checkout';
      toast.error(msg);
      setCheckoutLoading(false);
    }
  };

  const copyReferralLink = () => {
    const code = referralData?.referral?.code;
    if (code) {
      const link = `${window.location.origin}/subscription?ref=${code}`;
      navigator.clipboard.writeText(link);
      toast.success('Referral link copied!');
    }
  };

  const handleApplyCode = async () => {
    if (!referralCode.trim()) return;
    setApplyingCode(true);
    try {
      const res = await axios.post(`${API_V2}/referral/apply-code`, { code: referralCode.trim() }, { withCredentials: true });
      if (res.data.success) {
        toast.success(res.data.message);
        setReferralCode('');
        fetchSubscription();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid referral code');
    } finally {
      setApplyingCode(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#00FF41] animate-spin" />
      </div>
    );
  }

  const accessLevel = subscriptionData?.access_level || 'FREE';
  const sub = subscriptionData?.subscription;
  const tierCfg = TIER_CONFIG[accessLevel] || TIER_CONFIG.FREE;
  const isFreeTier = accessLevel === 'FREE';
  const isPaid = accessLevel === 'PAID';
  const isPrivileged = ['FAMILY', 'GOD'].includes(accessLevel);
  const expiresAt = sub?.expires_at ? new Date(sub.expires_at) : null;
  const daysLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  return (
    <div className="min-h-screen bg-black text-[#00FF41]">
      <AppBranding />
      <div className="p-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#00FF41] hover:bg-[#003B00]" data-testid="subscription-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <h1 className="text-2xl font-bold tracking-wider">SUBSCRIPTION</h1>
        </div>

        {/* Checking payment status */}
        {checkingPayment && (
          <Card className="bg-[#0a0a0a] border-yellow-600" data-testid="payment-checking-card">
            <CardContent className="p-6 flex items-center gap-4">
              <Loader2 className="h-6 w-6 text-yellow-400 animate-spin" />
              <div>
                <p className="text-yellow-400 font-bold">Verifying payment...</p>
                <p className="text-sm text-gray-400">This may take a few seconds.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Tier */}
        <Card className={`bg-[#0a0a0a] ${tierCfg.color}`} data-testid="current-tier-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              {tierCfg.icon}
              <span>YOUR TIER: {accessLevel}</span>
              <Badge className={`${tierCfg.badge} text-white ml-auto`}>{accessLevel}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isFreeTier && (
              <div>
                <p className="text-sm text-gray-400">0.001% fee on profitable trades</p>
                <p className="text-sm text-gray-500 mt-1">Upgrade to PAID to reduce fees by 10x</p>
              </div>
            )}
            {isPaid && sub?.status === 'active' && (
              <div>
                <p className="text-sm text-gray-400">0.0001% fee on profitable trades</p>
                <div className="flex items-center gap-2 mt-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-400">Active - {daysLeft} days remaining</span>
                </div>
                {expiresAt && <p className="text-xs text-gray-500 mt-1">Expires: {expiresAt.toLocaleDateString()}</p>}
              </div>
            )}
            {isPaid && sub?.status === 'expired' && (
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-500" />
                <span className="text-sm text-red-400">Subscription expired — renew below</span>
              </div>
            )}
            {isPrivileged && (
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm text-green-400">No fees, no limits</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tier Comparison */}
        <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="tier-comparison-card">
          <CardHeader>
            <CardTitle className="text-[#00FF41]">TIER COMPARISON</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {/* FREE */}
              <div className={`flex items-center justify-between p-3 border ${isFreeTier ? 'border-gray-400 bg-[#001a00]' : 'border-[#003B00]'}`}>
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-gray-400" />
                  <div>
                    <span className="font-bold text-gray-400">FREE</span>
                    <p className="text-xs text-gray-500">0.001% fee on profits</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">$0/mo</span>
              </div>
              {/* PAID */}
              <div className={`flex items-center justify-between p-3 border ${isPaid ? 'border-blue-400 bg-blue-950/30' : 'border-[#003B00]'}`}>
                <div className="flex items-center gap-3">
                  <Zap className="h-4 w-4 text-blue-400" />
                  <div>
                    <span className="font-bold text-blue-400">PAID</span>
                    <p className="text-xs text-gray-500">0.0001% fee on profits (10x less)</p>
                  </div>
                </div>
                <span className="text-sm text-blue-400 font-bold">$0.99/mo</span>
              </div>
              {/* FAMILY */}
              <div className="flex items-center justify-between p-3 border border-[#003B00]">
                <div className="flex items-center gap-3">
                  <Users className="h-4 w-4 text-purple-400" />
                  <div>
                    <span className="font-bold text-purple-400">FAMILY</span>
                    <p className="text-xs text-gray-500">No fees, no limits</p>
                  </div>
                </div>
                <span className="text-xs text-gray-600">Invite only</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upgrade / Renew Button */}
        {(isFreeTier || (isPaid && sub?.status === 'expired')) && (
          <Card className="bg-[#0a0a0a] border-blue-600" data-testid="upgrade-card">
            <CardContent className="p-6 text-center space-y-4">
              <DollarSign className="h-10 w-10 text-blue-400 mx-auto" />
              <h3 className="text-xl font-bold text-blue-400">
                {isFreeTier ? 'UPGRADE TO PAID' : 'RENEW SUBSCRIPTION'}
              </h3>
              <p className="text-sm text-gray-400">$0.99/month - Reduce your trading fees by 10x</p>
              <Button
                onClick={handleUpgrade}
                disabled={checkoutLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg"
                data-testid="upgrade-btn"
              >
                {checkoutLoading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Zap className="h-5 w-5 mr-2" />}
                {checkoutLoading ? 'Redirecting to Stripe...' : `Pay $0.99`}
              </Button>
              <p className="text-xs text-gray-600">30-day subscription. Cancel anytime.</p>
            </CardContent>
          </Card>
        )}

        {/* Referral System */}
        <Separator className="bg-[#003B00]" />
        <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="referral-card">
          <CardHeader>
            <CardTitle className="text-[#00FF41] flex items-center gap-2">
              <Gift className="h-5 w-5" /> REFERRAL PROGRAM
              <button
                onClick={() => setShowReferralInfo(true)}
                className="ml-auto text-gray-500 hover:text-[#00FF41] transition-colors"
                data-testid="referral-info-btn"
              >
                <Info className="h-4 w-4" />
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {referralData?.eligible ? (
              <>
                {/* Referral Code & Link */}
                <div className="border border-[#003B00] p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Your Referral Code</p>
                      <p className="text-xl font-bold font-mono tracking-wider" data-testid="referral-code">{referralData.referral?.code}</p>
                    </div>
                    <Button
                      onClick={copyReferralLink}
                      className="bg-[#003B00] hover:bg-[#004d00] text-[#00FF41] text-xs"
                      data-testid="copy-referral-btn"
                    >
                      <Copy className="h-3 w-3 mr-1.5" /> Copy Link
                    </Button>
                  </div>
                </div>

                {/* Progress */}
                <div className="border border-[#003B00] p-4 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Referrals Converted</span>
                    <span className="text-[#00FF41] font-bold font-mono" data-testid="referral-count">
                      {referralData.referral?.converted_count || 0} / {referralData.family_threshold}
                    </span>
                  </div>
                  <div className="w-full bg-[#001a00] h-3 border border-[#003B00]">
                    <div
                      className="h-full bg-[#00FF41] transition-all duration-500"
                      style={{ width: `${Math.min(((referralData.referral?.converted_count || 0) / referralData.family_threshold) * 100, 100)}%` }}
                      data-testid="referral-progress-bar"
                    />
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-gray-500">
                    <span>Free months earned: {referralData.referral?.free_months_earned || 0}</span>
                    {referralData.referral?.family_reward_active ? (
                      <span className="text-purple-400">FAMILY access active!</span>
                    ) : (
                      <span>{referralData.family_threshold} referrals = {referralData.family_reward_months} months FAMILY access</span>
                    )}
                  </div>
                </div>

                {/* Referred Users */}
                {referralData.referred_users?.length > 0 && (
                  <div className="border border-[#003B00] p-3">
                    <p className="text-[10px] text-gray-500 uppercase mb-2">Recent Referrals</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {referralData.referred_users.slice(0, 10).map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-[#003B00]/30">
                          <span className="text-gray-400">{r.referred_email || 'User'}</span>
                          <span className="text-gray-600">{r.converted_at ? new Date(r.converted_at).toLocaleDateString() : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4 space-y-3">
                <p className="text-sm text-gray-400">
                  {referralData ? 'Upgrade to PAID to unlock your referral code and start earning rewards!' : 'Loading referral data...'}
                </p>
                {/* Apply code section for non-eligible users */}
                <div className="border border-[#003B00] p-4 space-y-2">
                  <p className="text-[10px] text-gray-500 uppercase">Have a referral code?</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      placeholder="BB-XXXXXXXX"
                      className="flex-1 bg-black border border-[#003B00] text-[#00FF41] text-xs p-2 font-mono focus:outline-none focus:border-[#00FF41]"
                      data-testid="apply-referral-input"
                    />
                    <Button
                      onClick={handleApplyCode}
                      disabled={applyingCode || !referralCode.trim()}
                      className="bg-[#003B00] hover:bg-[#004d00] text-[#00FF41] text-xs"
                      data-testid="apply-referral-btn"
                    >
                      {applyingCode ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment History */}
        {subscriptionData?.payment_history?.length > 0 && (
          <>
            <Separator className="bg-[#003B00]" />
            <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="payment-history-card">
              <CardHeader>
                <CardTitle className="text-[#00FF41] flex items-center gap-2">
                  <Clock className="h-4 w-4" /> PAYMENT HISTORY
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {subscriptionData.payment_history.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm p-2 border-b border-[#003B00]/50">
                      <span className="text-gray-400">{new Date(p.created_at).toLocaleDateString()}</span>
                      <span className="font-mono">${p.amount?.toFixed(2)}</span>
                      <Badge className={p.payment_status === 'paid' ? 'bg-green-700 text-white' : p.payment_status === 'pending' ? 'bg-yellow-700 text-white' : 'bg-red-700 text-white'}>
                        {p.payment_status?.toUpperCase()}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Referral Info Dialog */}
        <Dialog open={showReferralInfo} onOpenChange={setShowReferralInfo}>
          <DialogContent className="bg-[#0a0a0a] border-[#003B00] text-[#00FF41] max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#00FF41]">
                <Gift className="h-5 w-5" /> Referral Program
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="border border-[#003B00] p-3 space-y-2">
                <p className="text-xs text-gray-500 uppercase font-bold">How it works</p>
                <ul className="space-y-1.5 text-gray-300 text-xs">
                  <li className="flex gap-2"><span className="text-[#00FF41]">1.</span> Share your unique referral code with friends</li>
                  <li className="flex gap-2"><span className="text-[#00FF41]">2.</span> When they subscribe to PAID, you earn rewards</li>
                  <li className="flex gap-2"><span className="text-[#00FF41]">3.</span> Each conversion = +1 free month on your subscription</li>
                </ul>
              </div>

              <div className="border border-[#003B00] p-3 space-y-2">
                <p className="text-xs text-gray-500 uppercase font-bold">Rewards</p>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Zap className="h-3 w-3 text-blue-400 flex-shrink-0" />
                    <span className="text-gray-300"><strong className="text-blue-400">Each referral</strong> — 1 free month added to your subscription</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-3 w-3 text-purple-400 flex-shrink-0" />
                    <span className="text-gray-300"><strong className="text-purple-400">100 referrals</strong> — 3 months of FAMILY access (no fees!)</span>
                  </div>
                </div>
              </div>

              <div className="border border-[#003B00] p-3 space-y-2">
                <p className="text-xs text-gray-500 uppercase font-bold">Rules</p>
                <ul className="space-y-1 text-gray-400 text-[11px]">
                  <li>- Referral count never expires until 100 is reached</li>
                  <li>- FAMILY upgrade lasts 3 full months, then reverts to your previous level</li>
                  <li>- FAMILY tier means zero trading fees during the upgrade period</li>
                  <li>- No limit on the number of referrals you can make</li>
                </ul>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
